/**
 * useViewportScale - 对内容层进行等比缩放以适配小屏
 *
 * 设计断点：1440x900（最大，不缩放）
 * 兼容断点：1280x800、1152x700
 *
 * 实现方式：
 * 在内容层 wrapper 上使用 transform: scale()，背景层不受影响。
 * wrapper 固定为设计稿尺寸 (1440x900)，scale 缩小后居中填满视口。
 * Reka Dialog/Tooltip 等 Portal 通过 ConfigProvider.teleportTo 挂到该 wrapper，才能一起缩放。
 *
 * 受 scale 影响的坐标计算文件（暂未修改，后续如有偏差可针对性修正）：
 * - src/composables/useSliderDrag/index.ts
 * - src/components/colorPicker/huePicker.vue
 * - src/components/colorPicker/svPicker.vue
 * - src/views/device/keyboard/keys/components/TabDKS/components/USlider.vue
 * - src/components/carousel/index.vue
 * - src/views/ElementTestView.vue
 */

import { ref, onMounted, onUnmounted } from 'vue'

const DESIGN_WIDTH = 1440
const DESIGN_HEIGHT = 900

/** 内容缩放层 DOM id，Reka ConfigProvider.teleportTo 挂到这里 */
export const APP_SCALE_ROOT_ID = 'app-scale-root'
export const APP_SCALE_ROOT_SELECTOR = `#${APP_SCALE_ROOT_ID}`

/**
 * 计算当前缩放比例
 */
function calcScale(): number {
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (vw >= DESIGN_WIDTH && vh >= DESIGN_HEIGHT) {
    return 1
  }

  return Math.min(vw / DESIGN_WIDTH, vh / DESIGN_HEIGHT, 1)
}

/**
 * 组合式函数：返回响应式 scale 值、视口尺寸
 */
export function useViewportScale() {
  const scale = ref(calcScale())
  const viewportWidth = ref(window.innerWidth)
  const viewportHeight = ref(window.innerHeight)

  const update = () => {
    scale.value = calcScale()
    viewportWidth.value = window.innerWidth
    viewportHeight.value = window.innerHeight
  }

  onMounted(() => {
    window.addEventListener('resize', update)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', update)
  })

  return { scale, viewportWidth, viewportHeight }
}

/**
 * 获取当前 scale（用于坐标修正）
 */
export function getCurrentScale(): number {
  return calcScale()
}

/**
 * 把视口坐标换算成 #app-scale-root 内 position:fixed 使用的 CSS 坐标。
 * 缩放层有 transform 时，fixed 相对该层而不是视口。
 */
export function viewportToScaleRootPoint(
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const scale = calcScale()
  if (scale >= 1) {
    return { x: clientX, y: clientY }
  }

  const root = document.getElementById(APP_SCALE_ROOT_ID)
  if (!root) {
    return { x: clientX, y: clientY }
  }

  const rect = root.getBoundingClientRect()
  return {
    x: (clientX - rect.left) / scale,
    y: (clientY - rect.top) / scale,
  }
}

/**
 * position:fixed 挂在 #app-scale-root 内时的可用宽高（该层布局 CSS 像素）。
 * 无缩放时 fixed 仍相对视口，返回视口尺寸。
 */
export function getScaleRootFixedBounds(): { width: number; height: number } {
  const scale = calcScale()
  if (scale >= 1) {
    return { width: window.innerWidth, height: window.innerHeight }
  }

  const root = document.getElementById(APP_SCALE_ROOT_ID)
  return {
    width: root?.clientWidth ?? window.innerWidth / scale,
    height: root?.clientHeight ?? window.innerHeight / scale,
  }
}
